import type { HTMLAttributes, ReactNode } from 'react'

interface TierProps extends HTMLAttributes<HTMLSpanElement> {
  /**
   * The payload's `evidence_tier`. `strong` and `limited` are toned; `moderate`
   * — and any string the backend has not shipped yet — is the muted default,
   * rather than a colour the client picked for it.
   */
  level: string
  children: ReactNode
}

/**
 * The evidence badge beside a score.
 *
 * It holds NO COPY: the caller passes the words, because naming an evidence
 * tier is the payload's job and not the client's. All this decides is which of
 * three tones the mono label is drawn in, and the words are always present, so
 * the tone is never the only channel.
 *
 * Green for `strong` and amber for `limited` reproduce what 1.9 already shipped
 * (`ScoreCard.tsx` mapped them onto `Pill` tones). That is a strength-of-
 * evidence vocabulary, not a score label — O3's ban on green/amber is about
 * labels, where a shift from baseline is neither good nor bad.
 */
export function Tier({ level, children, className, ...rest }: TierProps) {
  const classes = [
    'tier',
    level === 'strong' && 'tier--strong',
    level === 'limited' && 'tier--limited',
    className,
  ].filter(Boolean).join(' ')

  return <span {...rest} className={classes}>{children}</span>
}
